/**
 * Editor tools: connect an AI assistant to the Flux Agent editor MCP server, manage personal
 * access tokens and connected apps, and (for trainers) grant or revoke editors. Visible only to
 * editors and trainers; every decision and every command shown comes from the Rust Worker.
 *
 * @module apps/web/src/pages/EditorToolsPage
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, KeyRound, PlugZap, ShieldCheck, UserPlus, History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import {
  editorToolsApi,
  type CreatedEditorToken,
  type EditorAuditEntry,
  type EditorConnection,
  type EditorGrant,
  type EditorStatus,
} from '@/lib/editorToolsApi';

/** Format an RFC 3339 time for display, or a dash when absent. */
function when(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

/** Monospace block with a copy button.
 * @component CopyBlock
 */
function CopyBlock({
  text,
  label,
}: {
  /** Text shown and copied. */
  text: string;
  /** Accessible name of the copy button. */
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="bg-muted/50 overflow-x-auto whitespace-pre-wrap break-all rounded-md border p-3 pr-12 font-mono text-xs">
        {text}
      </pre>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label={label}
        className="absolute right-1 top-1 h-8 w-8"
        onClick={async () => {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

/** Show a toast for a failed request. */
function failed(message: string | undefined) {
  toast({
    title: 'Something went wrong',
    description: message || 'Try again.',
    variant: 'destructive',
  });
}

/** Editor tools page for editors and trainers. */
export function EditorToolsPage() {
  const [status, setStatus] = useState<EditorStatus | null>(null);
  const [connections, setConnections] = useState<EditorConnection[]>([]);
  const [editors, setEditors] = useState<EditorGrant[]>([]);
  const [audit, setAudit] = useState<EditorAuditEntry[]>([]);
  const [created, setCreated] = useState<CreatedEditorToken | null>(null);
  const [tokenName, setTokenName] = useState('');
  const [tokenDays, setTokenDays] = useState(30);
  const [grantEmail, setGrantEmail] = useState('');
  const [busy, setBusy] = useState(false);

  /** Load status, then whatever the caller's roles allow. */
  const load = useCallback(async () => {
    const result = await editorToolsApi.status();
    if (!result.success || !result.data) return failed(result.error?.message);
    setStatus(result.data);
    if (result.data.patDefaultDays) setTokenDays(result.data.patDefaultDays);
    const [connectionList, auditList, editorList] = await Promise.all([
      editorToolsApi.connections(),
      result.data.editor || result.data.admin ? editorToolsApi.audit() : null,
      result.data.admin ? editorToolsApi.editors() : null,
    ]);
    if (connectionList.success) setConnections(connectionList.data?.connections ?? []);
    if (auditList?.success) setAudit(auditList.data?.entries ?? []);
    if (editorList?.success) setEditors(editorList.data?.editors ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!status) {
    return <div className="text-muted-foreground p-6 text-sm">Loading editor tools…</div>;
  }
  if (!status.editor && !status.admin) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card>
          <CardHeader>
            <CardTitle>Editor tools</CardTitle>
            <CardDescription>
              Only authorised Flux Agent editors can change the system prompt and playbooks. Ask a
              Flux Agent trainer for access.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const setup = status.setup;
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Editor tools</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Connect your own AI assistant to edit the Flux Agent system prompt and playbooks by
          conversation. Every change is a reviewable draft until you approve it, and every change is
          recorded.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {status.editor && <Badge>Editor</Badge>}
          {status.admin && <Badge variant="secondary">Trainer (manages editors)</Badge>}
          {status.enabled === false && <Badge variant="destructive">Server turned off</Badge>}
        </div>
      </div>

      {status.editor && setup && status.mcpUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlugZap className="h-5 w-5" /> Connect your assistant
            </CardTitle>
            <CardDescription>
              Nothing to install: add this URL to your client. It opens fluxability.io in your
              browser to sign in and asks you to approve the connection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <CopyBlock text={status.mcpUrl} label="Copy server URL" />
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Claude Code</h3>
              <CopyBlock text={setup.claudeCode} label="Copy Claude Code command" />
              <p className="text-muted-foreground text-xs">
                Then run <code>/mcp</code> in Claude Code and choose Authenticate.
              </p>
            </section>
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Claude Desktop</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                {setup.claudeDesktop.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section className="space-y-2">
              <h3 className="text-sm font-medium">ChatGPT</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                {setup.chatgpt.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Other MCP clients</h3>
              <p className="text-muted-foreground text-xs">
                Any client that supports Streamable HTTP and MCP OAuth works with this config. Codes
                are only sent to your own computer (loopback) or to{' '}
                {(status.redirectHosts ?? []).join(', ')}.
              </p>
              <CopyBlock text={JSON.stringify(setup.mcpJson, null, 2)} label="Copy MCP JSON" />
            </section>
          </CardContent>
        </Card>
      )}

      {status.editor && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Personal access tokens
            </CardTitle>
            <CardDescription>
              For clients that cannot sign in with OAuth. Tokens expire, can be revoked here, and
              stop working if your editor access is removed or you sign out of Fluxability
              everywhere.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={async event => {
                event.preventDefault();
                setBusy(true);
                const result = await editorToolsApi.createToken(tokenName.trim(), tokenDays);
                setBusy(false);
                if (!result.success || !result.data) return failed(result.error?.message);
                setCreated(result.data);
                setTokenName('');
                void load();
              }}
            >
              <div className="flex-1 space-y-1">
                <Label htmlFor="editor-token-name">Name</Label>
                <Input
                  id="editor-token-name"
                  value={tokenName}
                  maxLength={80}
                  placeholder="Work laptop"
                  onChange={event => setTokenName(event.target.value)}
                  required
                />
              </div>
              <div className="w-32 space-y-1">
                <Label htmlFor="editor-token-days">Days</Label>
                <Input
                  id="editor-token-days"
                  type="number"
                  min={1}
                  max={status.patMaxDays ?? 90}
                  value={tokenDays}
                  onChange={event => setTokenDays(Number(event.target.value))}
                />
              </div>
              <Button type="submit" disabled={busy || !tokenName.trim()}>
                Create token
              </Button>
            </form>
            {created && (
              <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <p className="text-sm font-medium">
                  Copy “{created.name}” now. It will not be shown again.
                </p>
                <CopyBlock text={created.token} label="Copy token" />
                <p className="text-muted-foreground text-xs">Claude Code with this token:</p>
                <CopyBlock text={created.claudeCode} label="Copy Claude Code command with token" />
                <Button type="button" variant="ghost" size="sm" onClick={() => setCreated(null)}>
                  I saved it
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Your connections
          </CardTitle>
          <CardDescription>
            Apps you approved and tokens you created. Revoke any you no longer use.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connections.length === 0 ? (
            <p className="text-muted-foreground text-sm">No active connections.</p>
          ) : (
            <ul className="divide-y">
              {connections.map(connection => (
                <li
                  key={connection.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {connection.name || 'Unnamed'}{' '}
                      <Badge variant="outline">
                        {connection.type === 'oauth_app' ? 'App' : 'Token'}
                      </Badge>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Created {when(connection.createdAt)} · Last used {when(connection.lastUsedAt)}{' '}
                      · Expires {when(connection.expiresAt)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const result = await editorToolsApi.revokeConnection(connection.id);
                      if (!result.success) return failed(result.error?.message);
                      void load();
                    }}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {status.admin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Editors
            </CardTitle>
            <CardDescription>
              Editors can change the system prompt and playbooks for every user. Revoking access
              also revokes every connection they made.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={async event => {
                event.preventDefault();
                setBusy(true);
                const result = await editorToolsApi.grant(grantEmail.trim());
                setBusy(false);
                if (!result.success) return failed(result.error?.message);
                setGrantEmail('');
                void load();
              }}
            >
              <div className="flex-1 space-y-1">
                <Label htmlFor="editor-grant-email">Fluxability account email</Label>
                <Input
                  id="editor-grant-email"
                  type="email"
                  value={grantEmail}
                  onChange={event => setGrantEmail(event.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={busy || !grantEmail.trim()}>
                Grant editor access
              </Button>
            </form>
            <ul className="divide-y">
              {editors.map(editor => (
                <li
                  key={editor.userId}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {editor.email}{' '}
                      {editor.active ? (
                        <Badge>Active</Badge>
                      ) : (
                        <Badge variant="outline">Revoked</Badge>
                      )}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Granted {when(editor.grantedAt)} by {editor.grantedBy}
                      {editor.revokedAt &&
                        ` · Revoked ${when(editor.revokedAt)} by ${editor.revokedBy}`}
                    </p>
                  </div>
                  {editor.active && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm(`Revoke editor access for ${editor.email}?`)) return;
                        const result = await editorToolsApi.revokeEditor(editor.userId);
                        if (!result.success) return failed(result.error?.message);
                        void load();
                      }}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Recent activity
          </CardTitle>
          <CardDescription>
            Every draft, change, grant, sign-in and refusal, newest first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nothing yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {audit.map(entry => (
                <li key={entry.id} className="py-2">
                  <p>
                    <span className="font-mono text-xs">{entry.action}</span>{' '}
                    <Badge variant={entry.outcome === 'ok' ? 'secondary' : 'destructive'}>
                      {entry.outcome}
                    </Badge>{' '}
                    {entry.path && <span className="font-mono text-xs">{entry.path}</span>}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {when(entry.at)} · {entry.actor || 'unknown'}
                    {entry.errorCode && ` · ${entry.errorCode}`}
                    {entry.commit && ` · commit ${entry.commit.slice(0, 10)}`}
                  </p>
                  {entry.rationale && <p className="text-xs">{entry.rationale}</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
